import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import React, { useEffect } from 'react';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  // Scroll to top when cart page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const handleContinueShopping = () => {
    navigate('/', { state: { scrollToProducts: true } });
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-pink-40 py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          {/* ✅ Responsive heading */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 md:mb-8 font-display">
            Your Cart
          </h1>
          {/* ✅ Responsive empty cart card */}
          <div className="bg-white rounded-xl md:rounded-2xl p-8 md:p-12 shadow-lg">
            <p className="text-lg md:text-xl lg:text-2xl text-gray-600 mb-6 md:mb-8">Your cart is empty</p>
            <button
              onClick={handleContinueShopping}
              className="bg-rose-600 hover:bg-rose-700 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg text-base md:text-lg font-semibold transition"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pink-40 py-8 md:py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4">
        {/* ✅ Responsive heading */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 md:mb-8 text-center font-display">
          Your Cart
        </h1>

        {/* ✅ Responsive grid - Stack on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          
          {/* Cart Items */}
          <div className="md:col-span-2 space-y-3 md:space-y-4">
            {cartItems.map((item, index) => (
              <div 
                key={`${item.id}-${item.color}-${index}`} 
                className="bg-white rounded-lg md:rounded-xl p-4 md:p-6 shadow-lg flex gap-3 md:gap-6"
              >
                {/* ✅ Responsive image */}
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-20 h-20 md:w-28 md:h-28 lg:w-32 lg:h-32 object-cover rounded-lg flex-shrink-0"
                />

                {/* Details */}
                <div className="flex-1 min-w-0">
                  {/* ✅ Responsive text */}
                  <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-1 md:mb-2 truncate">
                    {item.name}
                  </h3>
                  {item.color && (
                    <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">
                      Color: {item.color}
                    </p>
                  )}
                  <p className="text-lg md:text-xl lg:text-2xl font-bold text-rose-600 mb-2 md:mb-4">
                    {item.price}
                  </p>

                  {/* ✅ Responsive quantity controls */}
                  <div className="flex items-center gap-2 md:gap-4">
                    <button
                      onClick={() => updateQuantity(item.id, item.customization, item.quantity - 1)}
                      className="w-7 h-7 md:w-8 md:h-8 rounded border-2 border-gray-300 hover:border-rose-600 flex items-center justify-center font-bold text-sm md:text-base"
                    >
                      −
                    </button>
                    <span className="text-base md:text-lg font-semibold w-8 text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.customization, item.quantity + 1)}
                      className="w-7 h-7 md:w-8 md:h-8 rounded border-2 border-gray-300 hover:border-rose-600 flex items-center justify-center font-bold text-sm md:text-base"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* ✅ Responsive remove button */}
                <button
                  onClick={() => removeFromCart(item.id, item.customization)}
                  className="text-gray-400 hover:text-red-600 transition self-start"
                >
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="md:col-span-1">
            {/* ✅ Not sticky on mobile, sticky on desktop */}
            <div className="bg-white rounded-lg md:rounded-xl p-4 md:p-6 shadow-lg md:sticky md:top-24">
              {/* ✅ Responsive heading */}
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
                Order Summary
              </h2>
              
              {/* ✅ Responsive summary */}
              <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                <div className="flex justify-between text-sm md:text-base">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm md:text-base">
                  <span className="text-gray-600">Delivery</span>
                  <span className="font-semibold">TBD</span>
                </div>
                <div className="border-t pt-2 md:pt-3 flex justify-between">
                  <span className="text-lg md:text-xl font-bold">Total</span>
                  <span className="text-lg md:text-xl font-bold text-rose-600">
                    ${getCartTotal().toFixed(2)}
                  </span>
                </div>
              </div>

              {/* ✅ Responsive buttons */}
              <button  
                onClick={() => navigate('/checkout')} 
                className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 md:py-4 rounded-lg md:rounded-xl text-base md:text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg mb-3"
              >
                Proceed to Checkout
              </button>

              <button
                onClick={handleContinueShopping}
                className="w-full border-2 border-gray-300 hover:border-rose-600 text-gray-700 py-3 md:py-4 rounded-lg md:rounded-xl text-base md:text-lg font-semibold transition mb-3"
              >
                Continue Shopping
              </button>

              <button
                onClick={clearCart}
                className="w-full text-red-600 hover:text-red-700 py-2 text-xs md:text-sm transition"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;